/**
 * Blog tarafı için ortak yardımcılar:
 *  - URL builder (UI dili + multilang toggle'a göre)
 *  - API fetch wrapper (lang param'ı otomatik ekler)
 *  - Tarih biçimleyici (lang'a göre Intl)
 */

const API_BASE = "/api/blog";
const SITE_BASE = "https://entegrepanel.net";

/**
 * UI dili → URL prefix.
 * TR default (prefix yok), EN ve AR ayrı path.
 */
export function langPrefix(lang) {
  if (lang === "en") return "/en";
  if (lang === "ar") return "/ar";
  return "";
}

/**
 * Blog liste sayfası path'i.
 * Örnek: blogListPath('en') → '/en/blog'
 */
export function blogListPath(lang) {
  return `${langPrefix(lang)}/blog`;
}

/**
 * Blog detay sayfası path'i.
 * Örnek: blogPostPath('tr', 'ornek-yazi') → '/blog/ornek-yazi'
 */
export function blogPostPath(lang, slug) {
  return `${langPrefix(lang)}/blog/${slug}`;
}

/**
 * Kategori sayfası path'i.
 */
export function blogCategoryPath(lang, slug) {
  return `${langPrefix(lang)}/blog/kategori/${slug}`;
}

/**
 * Etiket sayfası path'i.
 */
export function blogTagPath(lang, tag) {
  return `${langPrefix(lang)}/blog/etiket/${encodeURIComponent(tag)}`;
}

/**
 * Tam canonical URL üret.
 */
export function blogAbsoluteUrl(path) {
  return `${SITE_BASE}${path}`;
}

/**
 * API çağrısı — lang otomatik query param olarak eklenir.
 */
export async function blogFetch(endpoint, { lang, params = {}, signal } = {}) {
  const url = new URL(API_BASE + endpoint, window.location.origin);
  if (lang) url.searchParams.set("lang", lang);
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
  }
  const res = await fetch(url.toString().replace(window.location.origin, ""), { signal });
  if (!res.ok) {
    const err = new Error(`Blog API ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Görüntülenme +1 (POST) — fire-and-forget.
 */
export function blogIncrementView(postId) {
  if (!postId) return;
  fetch(`${API_BASE}/view/${postId}`, { method: "POST" }).catch(() => {});
}

/**
 * Tarih biçimleyici — lang'a göre yerelleştirilmiş "13 Nis 2026" tarzı.
 */
export function formatBlogDate(iso, lang = "tr") {
  if (!iso) return "";
  const date = new Date(iso.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return "";
  const localeMap = { tr: "tr-TR", en: "en-US", ar: "ar-SA" };
  try {
    return new Intl.DateTimeFormat(localeMap[lang] || "tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  } catch {
    return iso.split(" ")[0];
  }
}

/**
 * Okuma süresi metni (lokalize).
 */
export function readingTimeLabel(minutes, lang = "tr") {
  if (!minutes) return "";
  const labels = {
    tr: `${minutes} dk okuma`,
    en: `${minutes} min read`,
    ar: `${minutes} دقيقة قراءة`,
  };
  return labels[lang] || labels.tr;
}

/**
 * Görüntülenme sayısını biçimle (1.2k gibi).
 */
export function formatViews(views, lang = "tr") {
  const n = Number(views) || 0;
  if (n < 1000) return String(n);
  if (n < 1_000_000) return (n / 1000).toFixed(n < 10000 ? 1 : 0).replace(/\.0$/, "") + "k";
  return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
}
